import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Text from 'commons/Text';
import Colors from 'configs/Colors';

import useStyles from './styles';

const ShareLinkTable = ({
  rows,
  isPending,
  onCopy,
  onRevoke,
  onExtend,
  formatDateTime,
  formatExpiry,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const list = Array.isArray(rows) ? rows : [];

  const renderStatus = (row) => {
    const status = (row?.status || '').toLowerCase();
    const isActive = status === 'active' || status === '1' || status === 'true';
    const label = isActive ? t('active') : t('inactive');
    return (
      <Text
        type="xs"
        className={clsx(classes.statusText, {
          [classes.statusInactive]: !isActive,
        })}
      >
        {label}
      </Text>
    );
  };

  const getShareUrl = (row) => {
    if (row?.id) {
      const siteUrl = process.env.REACT_APP_SITE_URL;
      return `${siteUrl}/share/${row.id}`;
    }
    return '';
  };

  return (
    <Box className={classes.tableWrap}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow className={classes.headerRow}>
            <TableCell className={classes.headerCellLeft}>
              <Text className={classes.headerText}>{t('shared_links')}</Text>
            </TableCell>
            <TableCell className={classes.headerCell}>
              <Text className={classes.headerText}>{t('date_time_share')}</Text>
            </TableCell>
            <TableCell className={classes.headerCell}>
              <Text className={classes.headerText}>{t('expiry_date')}</Text>
            </TableCell>
            <TableCell className={classes.headerCell}>
              <Text className={classes.headerText}>{t('status')}</Text>
            </TableCell>
            <TableCell className={classes.headerCellAction}>
              <Text className={classes.headerText}>{t('action')}</Text>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {isPending && (
            <TableRow>
              <TableCell colSpan={5}>
                <Box className={classes.loadingRow}>
                  <CircularProgress size={20} />
                </Box>
              </TableCell>
            </TableRow>
          )}

          {!isPending && list.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Box className={classes.emptyRow}>
                  <Text
                    type="sm"
                    color={Colors.Grey7}
                  >
                    --
                  </Text>
                </Box>
              </TableCell>
            </TableRow>
          )}

          {!isPending &&
            list.map((row, idx) => {
              const shareUrl = getShareUrl(row);
              const title = row?.title || row?.name || row?.memorable_name || '{Memorable name}';
              const createdAt = row?.created_at || row?.created || row?.createdAt;
              const expiresAt = row?.expires_at || row?.expiresAt;
              const expirationType = row?.expiration_type;

              return (
                <TableRow
                  key={row?.id || idx}
                  className={classes.bodyRow}
                >
                  <TableCell className={classes.bodyCellLeft}>
                    <Text
                      type="sm"
                      color={Colors.Grey10}
                    >
                      {title}
                    </Text>
                    <Text
                      type="xs"
                      className={classes.linkText}
                      title={shareUrl}
                    >
                      {shareUrl || '--'}
                    </Text>
                  </TableCell>

                  <TableCell className={classes.bodyCell}>
                    <Text
                      type="xs"
                      color={Colors.Grey10}
                    >
                      {formatDateTime(createdAt)}
                    </Text>
                  </TableCell>

                  <TableCell className={classes.bodyCell}>
                    <Text
                      type="xs"
                      color={Colors.Grey10}
                    >
                      {formatExpiry(expiresAt, expirationType)}
                    </Text>
                  </TableCell>

                  <TableCell className={classes.bodyCell}>{renderStatus(row)}</TableCell>

                  <TableCell className={classes.bodyCellAction}>
                    <Box className={classes.actionGroup}>
                      <IconButton
                        size="small"
                        className={classes.iconBtn}
                        onClick={() => onRevoke?.(row)}
                      >
                        <CancelOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <div className={classes.divider} />
                      <IconButton
                        size="small"
                        className={classes.iconBtn}
                        onClick={() => onExtend?.(row)}
                      >
                        <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <div className={classes.divider} />
                      <IconButton
                        size="small"
                        className={classes.iconBtn}
                        onClick={() => onCopy(shareUrl)}
                        disabled={!shareUrl}
                      >
                        <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ShareLinkTable;
